import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickContactOptions = ({ onWhatsAppClick }) => {
  const contactOptions = [
    {
      id: 1,
      title: "New Player Guidance",
      description: "Get started with personalized onboarding and game recommendations",
      icon: "UserPlus",
      color: "success",
      message: "Hi! I\'m new to Nitin and would like guidance on getting started with gaming. Can you help me?",
      popular: true
    },
    {
      id: 2,
      title: "Game Booking & Access",
      description: "Reserve your spot in premium games and tournaments",
      icon: "Calendar",
      color: "primary",
      message: "Hello! I\'d like to book access to premium games. What options are available?",
      popular: true
    },
    {
      id: 3,
      title: "Account & Payment Support",
      description: "Assistance with deposits, withdrawals, and account management",
      icon: "CreditCard",
      color: "accent",
      message: "Hi! I need help with my account and payment options. Can you assist me?",
      popular: false
    },
    {
      id: 4,
      title: "VIP Services & Bonuses",
      description: "Exclusive offers, VIP programs, and premium benefits",
      icon: "Crown",
      color: "warning",
      message: "Hello! I\'m interested in VIP services and exclusive bonuses. What\'s available for me?",
      popular: true
    },
    {
      id: 5,
      title: "Strategy & Tips",
      description: "Expert gaming strategies and winning tips from professionals",
      icon: "Target",
      color: "primary",
      message: "Hi! I\'d like to get expert gaming strategies and tips to improve my gameplay. Can you help?",
      popular: false
    },
    {
      id: 6,
      title: "Technical Support",
      description: "App issues, login problems, and technical troubleshooting",
      icon: "Settings",
      color: "text-secondary",
      message: "Hello! I\'m experiencing technical issues and need support. Can you help me resolve this?",
      popular: false
    }
  ];

  const handleOptionClick = (message) => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/917704070169?text=${encodedMessage}`, '_blank');
  };

  return (
    <section className="py-16 lg:py-24 bg-surface-secondary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-royal rounded-full flex items-center justify-center shadow-royal">
              <Icon name="Zap" size={32} color="white" strokeWidth={2} />
            </div>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-gradient-royal mb-6">
            Quick Contact Options
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Choose your specific need and connect instantly with the right expert. Each option comes with pre-filled messages to get you started quickly.
          </p>
        </div>

        {/* Contact Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {contactOptions?.map((option) => (
            <div 
              key={option?.id}
              className="relative bg-surface rounded-card shadow-subtle hover:shadow-royal transition-royal hover-lift overflow-hidden group"
            >
              {/* Popular Badge */}
              {option?.popular && (
                <div className="absolute top-4 right-4 z-10">
                  <div className="bg-gradient-royal text-white px-3 py-1 rounded-full text-xs font-semibold shadow-royal">
                    Popular
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Icon */}
                <div className="mb-6">
                  <div className={`w-16 h-16 rounded-card flex items-center justify-center bg-gradient-to-br from-${option?.color}/10 to-accent/10 group-hover:shadow-glow transition-royal`}>
                    <Icon 
                      name={option?.icon} 
                      size={32} 
                      color={`var(--color-${option?.color})`}
                      strokeWidth={2}
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="mb-8">
                  <h3 className="text-xl font-heading font-bold text-text-primary mb-3 group-hover:text-primary transition-royal">
                    {option?.title}
                  </h3>
                  
                  <p className="text-text-secondary leading-relaxed">
                    {option?.description}
                  </p>
                </div>

                {/* Action Button */}
                <Button
                  variant="outline"
                  size="default"
                  onClick={() => handleOptionClick(option?.message)}
                  iconName="MessageCircle"
                  iconPosition="left"
                  fullWidth
                  className="group-hover:border-primary group-hover:text-primary"
                >
                  Start Chat
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Emergency Contact */}
        <div className="bg-gradient-to-r from-accent/10 via-accent/5 to-accent/10 rounded-card p-8 border-2 border-accent/20">
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-6 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center shadow-royal animate-pulse-golden">
                <Icon name="AlertCircle" size={32} color="white" strokeWidth={2} />
              </div>
              
              <div>
                <h3 className="text-2xl font-heading font-bold text-text-primary mb-2">
                  Urgent Gaming Support
                </h3>
                <p className="text-text-secondary">
                  Need immediate assistance with active games or account issues? Our priority support team is standing by.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4">
              <Button
                variant="default"
                size="lg"
                onClick={() => handleOptionClick("URGENT: I need immediate assistance with my gaming account. This is a priority support request.")}
                iconName="Phone"
                iconPosition="left"
                className="bg-accent hover:bg-accent/90 text-white hover:shadow-glow"
              >
                Emergency Support
              </Button>
              
              <div className="text-center">
                <div className="flex items-center space-x-2 text-sm font-medium text-accent">
                  <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
                  <span>Priority Response</span>
                </div>
                <p className="text-xs text-text-muted mt-1">
                  &lt; 30 seconds
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Stats */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="MessageSquare" size={24} color="white" strokeWidth={2} />
            </div>
            <p className="text-2xl font-bold text-text-primary mb-2">50K+</p>
            <p className="text-sm text-text-secondary">Monthly Conversations</p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Clock" size={24} color="white" strokeWidth={2} />
            </div>
            <p className="text-2xl font-bold text-text-primary mb-2">98%</p>
            <p className="text-sm text-text-secondary">Resolved in First Chat</p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Star" size={24} color="white" strokeWidth={2} />
            </div>
            <p className="text-2xl font-bold text-text-primary mb-2">4.9/5</p>
            <p className="text-sm text-text-secondary">Customer Satisfaction</p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Users" size={24} color="white" strokeWidth={2} />
            </div>
            <p className="text-2xl font-bold text-text-primary mb-2">24/7</p>
            <p className="text-sm text-text-secondary">Expert Availability</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuickContactOptions;